/**
 * Popup SSO relay for C4B embeds.
 *
 * IDP login pages refuse to render in a frame, so an embedded launch signs in through a popup. The
 * popup runs the normal top-level SSO flow; on the SSO return page it hands the single-use token to
 * the iframe that opened it (instead of consuming it itself, which would mint a first-party session
 * the iframe's partitioned cookie jar can't see). The iframe then redeems the token in-frame.
 */

export const SSO_RELAY_MESSAGE_TYPE = 'coursera:sso-popup-relay';

// Popup-internal marker only (the popup's own first-party sessionStorage); the token never touches storage.
const SSO_RELAY_STORAGE_KEY = 'ssoPopupRelayId';

export type SsoRelayMessage = {
  type: typeof SSO_RELAY_MESSAGE_TYPE;
  relayId: string;
  method: string;
  token: string;
};

export const createSsoRelayId = (): string => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

export const markSsoRelayPopup = (relayId: string): void => {
  try {
    window.sessionStorage.setItem(SSO_RELAY_STORAGE_KEY, relayId);
  } catch {
    // Storage blocked: the popup falls back to a normal top-level login.
  }
};

/**
 * Popup side, on the SSO return page. Posts the token to the opener iframe and returns true, or
 * returns false (normal login continues in this window) when this isn't a relay popup or the opener
 * is gone. The marker is consumed either way so a reload can't relay twice.
 */
export const relaySsoTokenToOpener = (method: string, token: string): boolean => {
  let relayId: string | null = null;
  try {
    relayId = window.sessionStorage.getItem(SSO_RELAY_STORAGE_KEY);
    window.sessionStorage.removeItem(SSO_RELAY_STORAGE_KEY);
  } catch {
    return false;
  }
  const { opener } = window;
  if (!relayId || !opener || opener === window) {
    return false;
  }
  const message: SsoRelayMessage = { type: SSO_RELAY_MESSAGE_TYPE, relayId, method, token };
  // Same-origin target: a non-Coursera opener never receives the token.
  opener.postMessage(message, window.location.origin);
  return true;
};

/** Iframe side: accepts only the relay from the popup this frame opened, for this attempt. */
export const isSsoRelayMessage = (
  event: MessageEvent,
  relayId: string,
  popup: Window | null
): event is MessageEvent<SsoRelayMessage> => {
  const data = event.data as Partial<SsoRelayMessage> | undefined;
  return (
    event.origin === window.location.origin &&
    popup != null &&
    event.source === popup &&
    data?.type === SSO_RELAY_MESSAGE_TYPE &&
    data.relayId === relayId &&
    typeof data.method === 'string' &&
    typeof data.token === 'string'
  );
};
